import axios from 'axios';
import { generateVideoFrames } from '../utils/videoPoster';
import { validateAuth } from './validationUtils.js';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks
const MAX_CONCURRENT_UPLOADS = 3;

const MEDIA_API_URL = process.env.REACT_APP_API_MEDIA_URL;

/**
 * Upload large files using signed URLs and chunked upload
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<string>} The uploaded file URL
 */
export const uploadLargeFileWithSignedUrl = async (file, onProgress = null) => {
  const token = validateAuth();
  
  try {

    // Step 1: Get signed URL from backend
    
    const startResponse = await axios.post(
      `${MEDIA_API_URL}/start-upload`,
      {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type
      },
      {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }
    );


    if (!startResponse.data.result) {
      throw new Error('Failed to get signed URL from backend');
    }

    const { uploadId, sasUrl } = startResponse.data.result;

    // Step 2: Upload file in chunks
    const chunks = Math.ceil(file.size / CHUNK_SIZE);
    const blockIds = [];
    let uploadedBytes = 0;


    // Upload chunks with concurrency control
    const uploadPromises = [];
    
    for (let i = 0; i < chunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      // Generate block ID (must be base64 encoded and same length for all blocks)
      const blockId = btoa(String(i).padStart(6, '0')).replace(/=/g, '');
      blockIds.push(blockId);


      // Limit concurrent uploads
      if (uploadPromises.length >= MAX_CONCURRENT_UPLOADS) {
        await Promise.race(uploadPromises);
      }

      const uploadPromise = uploadChunk(sasUrl, blockId, chunk)
        .then(() => {
          uploadedBytes += chunk.size;
          if (onProgress) {
            const percentage = Math.round((uploadedBytes / file.size) * 100);
            onProgress({ 
              percentage, 
              uploadedBytes, 
              totalBytes: file.size,
              chunksCompleted: Math.ceil(uploadedBytes / CHUNK_SIZE)
            });
          }
        })
        .finally(() => {
          // Remove completed promise from array
          const index = uploadPromises.indexOf(uploadPromise);
          if (index > -1) uploadPromises.splice(index, 1);
        });

      uploadPromises.push(uploadPromise);
    }

    // Wait for all chunks to complete
    await Promise.all(uploadPromises);
    
    // Step 3: Commit the upload
    const commitResponse = await axios.post(
      `${MEDIA_API_URL}/commit-upload`,
      {
        uploadId,
        blockIds,
        mimeType: file.type
      },
      {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }
    );

    // Extract URL: prefer explicit result.url, fall back to base blob URL from sasUrl
    let finalUrl;
    if (commitResponse.data.result && commitResponse.data.result.url) {
      finalUrl = commitResponse.data.result.url;
    } else if (commitResponse.data.url) {
      finalUrl = commitResponse.data.url;
    } else {
      // Derive public blob URL by stripping the SAS token query string
      finalUrl = sasUrl.split('?')[0];
    }

    if (!finalUrl) {
      throw new Error('Failed to commit upload: no URL returned');
    }

    // Video bytes can't be screened server-side, so sample frames here and let
    // the backend judge them.
    //
    // Only a 422 (content refused) fails the upload. Anything else — the
    // endpoint not deployed yet, a network blip, frames we couldn't capture —
    // leaves the video committed and awaiting review rather than blocking a
    // legitimate upload on an infrastructure problem.
    if (file.type && file.type.startsWith('video/')) {
      try {
        const frames = await generateVideoFrames(file);
        if (frames.length > 0) {
          await axios.post(
            `${MEDIA_API_URL}/screen-frames`,
            { uploadId, frames },
            { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
          );
        }
      } catch (screenError) {
        if (screenError.response?.status === 422) throw screenError;
        console.warn('Frame screening unavailable:', screenError.message);
      }
    }

    return finalUrl;

  } catch (error) {
    console.error('❌ Error in signed URL upload:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    }
    if (error.response?.status === 422) {
      const rejected = new Error(
        error.response.data?.message ||
          "This file can't be uploaded because of what it contains."
      );
      rejected.contentRejected = true;
      throw rejected;
    }
    throw new Error(`Signed URL upload failed: ${error.message}`);
  }
};

/**
 * Upload a single chunk to Azure Blob Storage using signed URL
 */
const uploadChunk = async (sasUrl, blockId, chunk) => {
  const maxRetries = 3;
  let retries = 0;

  while (retries < maxRetries) {
    try {
      const chunkUrl = `${sasUrl}&comp=block&blockid=${blockId}`;
      
      const response = await fetch(chunkUrl, {
        method: 'PUT',
        body: chunk,
        headers: {
          'Content-Length': chunk.size.toString()
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return;
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        throw error;
      }
      // Exponential backoff
      const backoffMs = Math.pow(2, retries) * 1000;
      await new Promise(resolve => setTimeout(resolve, backoffMs));
    }
  }
};

/**
 * Check if file needs signed URL upload.
 *
 * The chunked signed-URL path (4MB chunks, 3 concurrent, per-chunk retry with
 * backoff, direct-to-blob via SAS) is far more resilient on flaky mobile
 * connections than the single-shot backend POST, which restarts the whole file
 * on any drop and hops through an Azure Function. So we route through it for:
 *   - all videos, regardless of size (the uploads that actually fail), and
 *   - any file larger than 15MB.
 * Small images keep using the simple backend POST.
 */
export const needsSignedUrlUpload = (file) => {
  const isVideo = (file.type || '').toLowerCase().startsWith('video/');
  return isVideo || file.size > 15 * 1024 * 1024; // 15MB threshold
};

const signedUrlUploadService = {
  uploadLargeFileWithSignedUrl,
  needsSignedUrlUpload
};

export default signedUrlUploadService;
